import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH, faEnvelope, faHome, faPlus, faSearch, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { Breadcrumb, Button, ButtonGroup, Card, Col, Dropdown, Form, InputGroup, Modal, Pagination, Row, Table } from '@themesberg/react-bootstrap';
import ContentLoader from "react-content-loader";

import { deleteVendor, deleteVendorInvite, getVendors, inviteVendor, resendVendorInvite } from "../api/vendors.api";
import ConfirmDialog from "../components/ConfirmDialog";
import { useToast } from "../components/ToastProvider";

const PAGE_SIZE = 10;

const VendorTableSkeleton = () => (
  <>
    {[...Array(PAGE_SIZE)].map((_, index) => (
      <tr key={index}>
        <td><div className="skeleton-text" style={{ width: "20px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "120px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "160px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "100px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "60px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "140px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "140px", height: "12px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "80px", height: "20px" }} /></td>
        <td><div className="skeleton-text" style={{ width: "30px", height: "20px" }} /></td>
      </tr>
    ))}
  </>
);

export default () => {
  const { showToast } = useToast();
  const [vendors, setVendors] = useState([]);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1
  });
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [actionRowId, setActionRowId] = useState(null);
  const [pendingDeleteVendor, setPendingDeleteVendor] = useState(null);

  const loadVendors = useCallback(async () => {
    setLoading(true);

    try {
      const vendorsResponse = await getVendors({
        page,
        limit: PAGE_SIZE,
        search: searchQuery
      });

      const rows = (vendorsResponse.data || []).map((row) => ({
        ...row,
        invitedAt: formatDateTime(row.invitedAt),
        expiresAt: formatDateTime(row.expiresAt)
      }));

      setVendors(rows);
      setPagination(vendorsResponse.pagination || {
        page: 1,
        limit: PAGE_SIZE,
        total: 0,
        totalPages: 1
      });
    } catch (error) {
      showToast({ message: error.message || "Failed to load vendors", variant: "danger" });
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, showToast]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setSearchQuery(searchInput);
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > pagination.totalPages || nextPage === page) {
      return;
    }

    setPage(nextPage);
  };

  const openInviteModal = () => {
    setInviteEmail("");
    setShowInviteModal(true);
  };

  const closeInviteModal = () => {
    if (!inviteSubmitting) {
      setShowInviteModal(false);
    }
  };

  const formatDateTime = (dateValue) => {
    if (!dateValue) {
      return "N/A";
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return "N/A";
    }

    return date.toLocaleString();
  };

  const handleInviteSubmit = async (event) => {
    event.preventDefault();
    setInviteSubmitting(true);

    try {
      const response = await inviteVendor({ email: inviteEmail });
      showToast({ message: response.message || "Vendor invitation sent successfully.", variant: "success" });
      setShowInviteModal(false);
      setInviteEmail("");
      await loadVendors();
    } catch (error) {
      showToast({ message: error.message || "Failed to invite vendor", variant: "danger" });
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleResendInvite = async (inviteId) => {
    setActionRowId(`invite-${inviteId}`);

    try {
      const response = await resendVendorInvite(inviteId);
      showToast({ message: response.message || "Invitation email resent successfully.", variant: "success" });
      await loadVendors();
    } catch (error) {
      showToast({ message: error.message || "Failed to resend invitation email", variant: "danger" });
    } finally {
      setActionRowId(null);
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    setActionRowId(`invite-${inviteId}`);

    try {
      const response = await deleteVendorInvite(inviteId);
      showToast({ message: response.message || "Invitation deleted successfully.", variant: "success" });
      await loadVendors();
    } catch (error) {
      showToast({ message: error.message || "Failed to delete invitation", variant: "danger" });
    } finally {
      setActionRowId(null);
    }
  };

  const handleDeleteVendor = async () => {
    if (!pendingDeleteVendor) return;
    const vendorId = pendingDeleteVendor.vendorId;
    setActionRowId(`vendor-${vendorId}`);

    try {
      const response = await deleteVendor(vendorId);
      showToast({ message: response.message || "Vendor deleted successfully.", variant: "success" });
      await loadVendors();
    } catch (error) {
      showToast({ message: error.message || "Failed to delete vendor", variant: "danger" });
    } finally {
      setActionRowId(null);
      setPendingDeleteVendor(null);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <Breadcrumb className="d-none d-md-inline-block" listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}>
            <Breadcrumb.Item><FontAwesomeIcon icon={faHome} /></Breadcrumb.Item>
            <Breadcrumb.Item active>Vendors</Breadcrumb.Item>
          </Breadcrumb>
          <h4>Vendors</h4>
          <p className="mb-0">Manage registered vendors from the admin panel.</p>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <Button variant="primary" onClick={openInviteModal}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Invite Vendor
          </Button>
        </div>
      </div>

      <div className="table-settings mb-4">
        <Row className="justify-content-between align-items-center">
          <Col xs={12} md={6} lg={4}>
            <Form onSubmit={handleSearchSubmit}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search vendors"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
                <Button type="submit" variant="primary">
                  Search
                </Button>
              </InputGroup>
            </Form>
          </Col>
        </Row>
      </div>
      <Card border="light" className="table-wrapper table-responsive shadow-sm">
        <Card.Body className="pt-0">
          <Table hover className="user-table align-items-center">
            <thead>
              <tr>
                <th className="border-bottom">#</th>
                <th className="border-bottom">Name</th>
                <th className="border-bottom">Email</th>
                <th className="border-bottom">Contact</th>
                <th className="border-bottom">Role</th>
                <th className="border-bottom">Invited On</th>
                <th className="border-bottom">Expires On</th>
                <th className="border-bottom">Status</th>
                <th className="border-bottom">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <VendorTableSkeleton />
              ) : vendors.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-5">
                    No vendors found.
                  </td>
                </tr>
              ) : (
                vendors.map((vendor, index) => (
                  <tr key={vendor.id}>
                    <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                    <td>{vendor.name}</td>
                    <td>{vendor.email}</td>
                    <td>{vendor.contact}</td>
                    <td>{vendor.role}</td>
                    <td>{vendor.invitedAt}</td>
                    <td>{vendor.expiresAt}</td>
                    <td>{vendor.status}</td>
                    <td>
                      {vendor.type === "pending" ? (
                        <Dropdown as={ButtonGroup}>
                          <Dropdown.Toggle as={Button} split variant="link" className="text-dark m-0 p-0">
                            <span className="icon icon-sm">
                              <FontAwesomeIcon icon={faEllipsisH} className="icon-dark" />
                            </span>
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              onClick={() => handleResendInvite(vendor.inviteId)}
                              disabled={actionRowId === vendor.id}
                            >
                              <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                              Resend Email
                            </Dropdown.Item>
                            <Dropdown.Item
                              className="text-danger"
                              onClick={() => handleDeleteInvite(vendor.inviteId)}
                              disabled={actionRowId === vendor.id}
                            >
                              <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                              Delete
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      ) : vendor.type === "registered" ? (
                        <Dropdown as={ButtonGroup}>
                          <Dropdown.Toggle as={Button} split variant="link" className="text-dark m-0 p-0">
                            <span className="icon icon-sm">
                              <FontAwesomeIcon icon={faEllipsisH} className="icon-dark" />
                            </span>
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              className="text-danger"
                              onClick={() => setPendingDeleteVendor({
                                id: vendor.id,
                                vendorId: Number(String(vendor.id).replace("vendor-", "")),
                                name: vendor.name
                              })}
                              disabled={actionRowId === vendor.id}
                            >
                              <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                              Delete
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      ) : (
                        "N/A"
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <Card.Footer className="px-3 border-0">
            <div className="d-lg-flex align-items-center justify-content-between">
              <Pagination className="mb-2 mb-lg-0">
                <Pagination.Prev
                  disabled={loading || pagination.page <= 1}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  Previous
                </Pagination.Prev>
                <Pagination.Item active>{pagination.page}</Pagination.Item>
                <Pagination.Next
                  disabled={loading || pagination.page >= pagination.totalPages}
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  Next
                </Pagination.Next>
              </Pagination>
              <small className="fw-bold">
                Showing <b>{vendors.length}</b> of <b>{pagination.total}</b> total rows
              </small>
            </div>
          </Card.Footer>
        </Card.Body>
      </Card>

      <Modal as={Modal.Dialog} centered show={showInviteModal} onHide={closeInviteModal}>
        <Modal.Header>
          <Modal.Title className="h6">Invite Vendor</Modal.Title>
          <Button variant="close" aria-label="Close" onClick={closeInviteModal} disabled={inviteSubmitting} />
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleInviteSubmit}>
            <Form.Group className="mb-3" id="vendorInviteEmail">
              <Form.Label>Vendor Email</Form.Label>
              <Form.Control
                required
                type="email"
                placeholder="vendor@example.com"
                value={inviteEmail}
                onChange={(event) => setInviteEmail(event.target.value)}
              />
            </Form.Group>
            <div className="d-flex justify-content-end">
              <Button variant="outline-gray-600" className="me-2" onClick={closeInviteModal} disabled={inviteSubmitting}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={inviteSubmitting}>
                {inviteSubmitting ? "Sending..." : "Send Invite"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      <ConfirmDialog
        show={Boolean(pendingDeleteVendor)}
        title={pendingDeleteVendor ? `Delete ${pendingDeleteVendor.name}` : "Delete Vendor"}
        body="This will remove the registered vendor from the system. Do you want to continue?"
        confirmLabel="Delete Vendor"
        confirmVariant="danger"
        processing={Boolean(pendingDeleteVendor) && actionRowId === `vendor-${pendingDeleteVendor?.vendorId}`}
        onCancel={() => setPendingDeleteVendor(null)}
        onConfirm={handleDeleteVendor}
      />
    </>
  );
};
